/**
 * DontForgetBanner — iter87.180 Item E.
 *
 * Persistent, sessionStorage-dismissible nudge above the DRAFT section
 * for non-admins. Feature-aware copy:
 *
 *   Pre-season only  → "Don't forget: Rank your players and/or make
 *                       your Pre-season Picks prior to the start of
 *                       the draft or the beginning of the season."
 *
 *   In-season only   → "Don't forget: Make your In-season Picks every
 *                       week throughout the season."
 *
 *   Both applicable  → Combined copy (both variants above).
 *
 * Auto-hides once:
 *   - the tournament's first regular-season game has kicked off, AND
 *   - the user's pre-season picks are complete
 *
 * (For pre-season-only tournaments there is no in-season component to
 * wait for, so completion of the pre-season picks alone is enough.
 * For in-season-only tournaments there is no pre-season component to
 * wait for, so first kickoff is enough.)
 *
 * Same sessionStorage-dismissal pattern as iter87.174's StartDraftBanner
 * so the banner returns on every re-entry (fresh login, tab reopen,
 * Pro+ / Tester surface switch) rather than being permanently silenced.
 */
import { useState, useMemo } from "react";
import { AlertTriangle, X } from "lucide-react";
import {
    isGolfPickem, isSeasonFormat, myPicksForTournament, potPickInfo,
} from "@/lib/tournamentFormat";
import { isBoostOnlyTemplate } from "@/lib/boostOnlyTemplates";
import { isSquaresTemplate } from "@/components/dashboard/SquaresPanel";

const KEY = (leagueId, tid) => `tcr_dont_forget_banner_${leagueId}_${tid || "none"}_dismissed`;

const RANKABLE_TEMPLATES = new Set([
    "custom",
    "premier_league_api",
    "la_liga_api",
    "club_season_api",
    "nfl_season_api",
]);

function computeVariant(tournament, boost) {
    const tpl = tournament?.template;
    const isBoostOnly = isBoostOnlyTemplate(tpl);
    const isSeason = isSeasonFormat(tournament);
    const isGolfPk = isGolfPickem(tournament);
    const isNwslPlayoffs = tpl === "nwsl_playoffs_api";
    const isMlsPlayoffs = tpl === "mls_playoffs_api";
    const isUclPlayoffs = tpl === "ucl_playoffs_api";
    const isPot = tournament?.pickem?.mode === "pot" || isGolfPk;
    const potChampBonus = tournament?.pickem?.champions_pick_bonus || 0;
    const seasonHasChampTile = (tpl === "nfl_season_api") && potChampBonus > 0;
    const showRank = RANKABLE_TEMPLATES.has(tpl)
        && (tournament?.teams?.length > 0)
        && tournament?.pickem?.mode !== "pot";
    const hasChampionPick = (!isSeason && !isGolfPk && !isNwslPlayoffs && !isMlsPlayoffs && !isUclPlayoffs) || seasonHasChampTile;
    const hasPowerPick = !!tournament?.power_pick_enabled;
    const hasPreSeason = showRank || isPot || hasChampionPick || hasPowerPick || isNwslPlayoffs || isMlsPlayoffs || isUclPlayoffs;
    const hasInSeason = isBoostOnly || !!boost?.enabled || isNwslPlayoffs || isMlsPlayoffs || isUclPlayoffs;
    return { hasPreSeason, hasInSeason };
}

function computePreSeasonComplete(tournament, me, board) {
    const isGolfPk = isGolfPickem(tournament);
    const isPot = tournament?.pickem?.mode === "pot" || isGolfPk;
    const tpl = tournament?.template;
    const isSeason = isSeasonFormat(tournament);
    const isNwslPlayoffs = tpl === "nwsl_playoffs_api";
    const isMlsPlayoffs = tpl === "mls_playoffs_api";
    const isUclPlayoffs = tpl === "ucl_playoffs_api";
    const potChampBonus = tournament?.pickem?.champions_pick_bonus || 0;
    const seasonHasChampTile = (tpl === "nfl_season_api") && potChampBonus > 0;
    const showRank = RANKABLE_TEMPLATES.has(tpl)
        && (tournament?.teams?.length > 0)
        && tournament?.pickem?.mode !== "pot";
    const hasChampionPick = (!isSeason && !isGolfPk && !isNwslPlayoffs && !isMlsPlayoffs && !isUclPlayoffs) || seasonHasChampTile;
    const hasPowerPick = !!tournament?.power_pick_enabled;

    if (showRank) {
        const ranking = me?.team_preference_ranking || [];
        if (!ranking.length) return false;
    }
    if (isPot) {
        const required = potPickInfo(tournament).picksRequired || 0;
        const myPicks = myPicksForTournament(me, tournament) || [];
        if (myPicks.length < required) return false;
    }
    if (hasChampionPick && !me?.knockout_pick_team_id) return false;
    if (hasPowerPick) {
        const myRow = board?.find?.((r) => r.participant_id === me?.id);
        const pickTeamId = myRow?.power_pick_team_id || me?.power_pick_team_id;
        if (!pickTeamId) return false;
    }
    if (isNwslPlayoffs) {
        if (!((me?.nwsl_champion_by_tournament || {})[tournament.id] || []).length) return false;
        if (!(me?.nwsl_darkhorse_by_tournament || {})[tournament.id]) return false;
    }
    if (isMlsPlayoffs) {
        if (!((me?.mls_champion_by_tournament || {})[tournament.id] || []).length) return false;
        if (!(me?.mls_darkhorse_by_tournament || {})[tournament.id]) return false;
    }
    if (isUclPlayoffs) {
        if (!((me?.ucl_champion_by_tournament || {})[tournament.id] || []).length) return false;
        if (!(me?.ucl_darkhorse_by_tournament || {})[tournament.id]) return false;
    }
    return true;
}

function computeSeasonStarted(tournament, boost, matches) {
    if (tournament?.round_started?.regular) return true;
    if (tournament?.round_started?.group) return true;
    if (tournament?.round_started?.r64) return true;
    if (tournament?.round_started?.r32) return true;
    if (tournament?.lifecycle_state === "live" || tournament?.lifecycle_state === "final" || tournament?.lifecycle_state === "completed") return true;
    if ((matches || []).some((m) => m.played || m.finished)) return true;
    const fixtures = boost?.state?.fixtures || [];
    if (fixtures.some((f) => f.started || f.finished)) return true;
    // iter87.361 — NWSL Playoffs: Round 1 (QF) has no `boost`/`matches`
    // feed at all, so check its own self-contained fixtures directly.
    const nwslFixtures = tournament?.nwsl?.fixtures || [];
    if (nwslFixtures.some((f) => f.played || (f.scheduled_at && new Date(f.scheduled_at).getTime() <= Date.now()))) return true;
    // iter87.465.20 — MLS Playoffs: check its Wild Card round games directly.
    const mlsWildcard = (tournament?.mls?.rounds || []).find((r) => r.key === "wildcard");
    const mlsGames = (mlsWildcard?.series || []).flatMap((s) => s.games || []);
    if (mlsGames.some((g) => g.played || (g.scheduled_at && new Date(g.scheduled_at).getTime() <= Date.now()))) return true;
    // iter87.466.1 — UCL Playoffs: check its Knockout Round Play-offs directly.
    const uclKo = (tournament?.ucl?.rounds || []).find((r) => r.key === "ko_playoff");
    const uclGames = (uclKo?.series || []).flatMap((s) => s.games || []);
    if (uclGames.some((g) => g.played || (g.scheduled_at && new Date(g.scheduled_at).getTime() <= Date.now()))) return true;
    return false;
}

export default function DontForgetBanner({ leagueId, tournament, me, board, boost, matches }) {
    const tid = tournament?.id;
    const [dismissed, setDismissed] = useState(() => {
        try { return !!sessionStorage.getItem(KEY(leagueId, tid)); } catch { return false; }
    });

    const { hasPreSeason, hasInSeason } = useMemo(
        () => computeVariant(tournament, boost),
        [tournament, boost]
    );
    const preSeasonComplete = useMemo(
        () => computePreSeasonComplete(tournament, me, board),
        [tournament, me, board]
    );
    const seasonStarted = useMemo(
        () => computeSeasonStarted(tournament, boost, matches),
        [tournament, boost, matches]
    );

    if (!tournament || !tid) return null;
    if (!me || me.is_admin) return null; // non-admins only
    if (dismissed) return null;
    if (tournament?.completion_authorized_by) return null;
    if (tournament?.lifecycle_state === "final" || tournament?.lifecycle_state === "completed") return null;
    if (!hasPreSeason && !hasInSeason) return null;

    // Auto-hide condition. Only the applicable halves need to resolve.
    const preSeasonDone = !hasPreSeason || preSeasonComplete;
    const inSeasonDone = !hasInSeason || seasonStarted;
    if (preSeasonDone && inSeasonDone) return null;

    const preLine = isGolfPickem(tournament)
        ? "Make your Pre-Season picks prior to the start of the first tee."
        : isSquaresTemplate(tournament?.template)
            ? "Make your Pre-Season picks prior to the start of the first/opening game."
            : "Rank your players and/or make your Pre-season Picks prior to the start of the draft or the beginning of the season.";
    const inLine = "Make your In-season Picks every week throughout the season.";

    let body;
    if (hasPreSeason && hasInSeason) {
        body = (
            <>
                <p className="text-xs text-zinc-300 leading-relaxed" data-testid="dont-forget-banner-pre-line">
                    <strong className="text-white">Pre-season:</strong> {preLine}
                </p>
                <p className="text-xs text-zinc-300 leading-relaxed mt-1" data-testid="dont-forget-banner-in-line">
                    <strong className="text-white">In-season:</strong> {inLine}
                </p>
            </>
        );
    } else if (hasPreSeason) {
        body = (
            <p className="text-xs text-zinc-300 leading-relaxed" data-testid="dont-forget-banner-pre-line">
                {preLine}
            </p>
        );
    } else {
        body = (
            <p className="text-xs text-zinc-300 leading-relaxed" data-testid="dont-forget-banner-in-line">
                {inLine}
            </p>
        );
    }

    const dismiss = () => {
        try { sessionStorage.setItem(KEY(leagueId, tid), "1"); } catch { /* ignore */ }
        setDismissed(true);
    };

    return (
        <div
            className="brutal-card-light p-4 flex flex-wrap items-start justify-between gap-3 border-cyber/40"
            data-testid="dont-forget-banner"
        >
            <div className="flex items-start gap-3 flex-1 min-w-0">
                <div className="w-8 h-8 bg-cyber border-2 border-black shadow-brutal flex items-center justify-center shrink-0">
                    <AlertTriangle className="w-4 h-4 text-black" strokeWidth={2.5} />
                </div>
                <div className="min-w-0 flex-1">
                    <p className="font-bold text-sm">Don't forget</p>
                    {body}
                </div>
            </div>
            <button
                onClick={dismiss}
                className="brutal-btn-ghost p-2"
                aria-label="Dismiss"
                data-testid="dismiss-dont-forget-banner-btn"
            >
                <X className="w-4 h-4" />
            </button>
        </div>
    );
}
